/**
 * Role utility functions for displaying role information consistently across the application
 */

/**
 * Get a human-readable display name for a role
 */
export function getRoleDisplayName(role: string): string {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'org_admin': return 'Organisation Admin';
      case 'rest_admin': return 'Restaurant Admin';
      case 'user': return 'User';
      case 'new_sign_up': return 'New Sign Up';
      case 'in_progress': return 'In Progress';
      default: return role || 'Unknown';
    }
  }
  
  /**
   * Get a badge variant for a role for consistent styling
   */
  export function getRoleBadgeVariant(role: string): string {
    switch (role) {
      case 'super_admin': return 'destructive';
      case 'org_admin': return 'default';
      case 'rest_admin': return 'outline';
      case 'user': return 'secondary';
      case 'new_sign_up': return 'outline';
      case 'in_progress': return 'outline';
      default: return 'secondary';
    }
  }
  
  /**
   * Get a description for a role's capabilities
   */
  export function getRoleDescription(role: string): string {
    switch (role) {
      case 'super_admin': 
        return 'Full system access - company employees only.';
      case 'org_admin': 
        return 'Can manage all restaurants in an organisation.';
      case 'rest_admin': 
        return 'Can manage specific restaurant settings and users.';
      case 'user': 
        return 'Basic access to assigned restaurants.';
      case 'new_sign_up': 
        return 'User who has just signed up and needs onboarding.';
      case 'in_progress': 
        return 'User who is in the middle of the onboarding process.';
      default: 
        return 'Unknown role';
    }
  }
  
  /**
   * Get a numeric value for role precedence (higher is more access)
   */
  export function getRolePrecedence(role: string): number {
    switch (role) {
      case 'super_admin': return 100;
      case 'org_admin': return 75;
      case 'rest_admin': return 50;
      case 'user': return 25;
      case 'new_sign_up': return 5;
      case 'in_progress': return 10;
      default: return 0;
    }
  }
  
  /**
   * Check if a role can modify another role
   */
  export function canModifyRole(modifierRole: string, targetRole: string): boolean {
    // Only super_admin can modify super_admin
    if (targetRole === 'super_admin' && modifierRole !== 'super_admin') {
      return false;
    }
    
    // Check if modifier has higher precedence
    return getRolePrecedence(modifierRole) > getRolePrecedence(targetRole);
  }
  
  /**
   * Check if a role has access to a feature
   */
  export function hasRoleAccess(role: string, requiredRole: string): boolean {
    return getRolePrecedence(role) >= getRolePrecedence(requiredRole);
  }